import { BreedingPair } from '../api/breeding';
import { format } from 'date-fns';
import BreedingPairAvatars from './breeding/BreedingPairAvatars';
import BreedingPairGeneticsPanel from './genetics/BreedingPairGeneticsPanel';
import BreedingPairOutcomesPanel from './genetics/BreedingPairOutcomesPanel';
import { GENDER_ICON_FEMALE, GENDER_ICON_MALE } from '../utils/genderDisplay';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface ViewBreedingPairModalProps {
  isOpen: boolean;
  onClose: () => void;
  breedingPair: BreedingPair;
}

export default function ViewBreedingPairModal({ isOpen, onClose, breedingPair }: ViewBreedingPairModalProps) {
  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Breeding Pair Details" onClose={onClose} />
      <BrandModalBody>
        {/* Breeding Pair Avatars */}
        <div className="flex justify-center">
          <BreedingPairAvatars
            maleBird={breedingPair.maleBirdEntity}
            femaleBird={breedingPair.femaleBirdEntity}
            size="lg"
            showBandIds={true}
            bandIdPosition="below"
            layout="horizontal"
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <div key="pair-info">
            <h4 className="text-sm font-medium text-slate-500">Birds</h4>
            <div className="mt-1 text-sm text-slate-900">
              <div className="flex items-center justify-between">
                <span>
                  <span className="text-cyan-600 font-medium">{GENDER_ICON_MALE} Male:</span> {breedingPair.maleBirdEntity?.bandId || breedingPair.maleBirdId}
                  {breedingPair.maleBirdEntity?.species && (
                    <span className="text-slate-500 ml-2">({breedingPair.maleBirdEntity.species.commonName})</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span>
                  <span className="text-pink-600 font-medium">{GENDER_ICON_FEMALE} Female:</span> {breedingPair.femaleBirdEntity?.bandId || breedingPair.femaleBirdId}
                  {breedingPair.femaleBirdEntity?.species && (
                    <span className="text-slate-500 ml-2">({breedingPair.femaleBirdEntity.species.commonName})</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div key="pairing-dates">
            <h4 className="text-sm font-medium text-slate-500">Pairing Dates</h4>
            <p className="mt-1 text-sm text-slate-900">
              Begin: {breedingPair.pairingBeginDate ? format(new Date(breedingPair.pairingBeginDate), 'MMM d, yyyy') : 'No date set'} <br />
              {breedingPair.pairingEndDate && (
                <>End: {format(new Date(breedingPair.pairingEndDate), 'MMM d, yyyy')}</>
              )}
            </p>
          </div>

          <div key="status">
            <h4 className="text-sm font-medium text-slate-500">Status</h4>
            <p className="mt-1 text-sm text-slate-900">
              {breedingPair.pairingEndDate ? 'Ended' : 'Active'}
            </p>
          </div>

          <div key="cage-number">
            <h4 className="text-sm font-medium text-slate-500">Cage Number</h4>
            <p className="mt-1 text-sm text-slate-900">{breedingPair.cageNumber}</p>
          </div>

          {breedingPair.comments && (
            <div key="comments">
              <h4 className="text-sm font-medium text-slate-500">Comments</h4>
              <p className="mt-1 text-sm text-slate-900">{breedingPair.comments}</p>
            </div>
          )}

          <div key="hybridization">
            <h4 className="text-sm font-medium text-slate-500">Hybridization</h4>
            <p className="mt-1 text-sm text-slate-900">
              {breedingPair.allowHybridization ? 'Allowed' : 'Not Allowed'}
            </p>
          </div>

          <div key="clutches">
            <h4 className="text-sm font-medium text-slate-500">Clutches</h4>
            <div className="mt-1 space-y-2">
              {breedingPair.clutches?.length > 0 ? (
                breedingPair.clutches.map((clutch) => (
                  <div key={clutch.id} className="text-sm text-slate-900">
                    <p>Date: {format(new Date(clutch.date), 'MMM d, yyyy')}</p>
                    <p>Eggs: {clutch.eggs?.length || 0}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No clutches recorded</p>
              )}
            </div>
          </div>
        </div>

        <BreedingPairGeneticsPanel
          breedingPairId={breedingPair.id}
          maleBirdId={breedingPair.maleBirdId}
          femaleBirdId={breedingPair.femaleBirdId}
          speciesId={breedingPair.maleBirdEntity?.speciesId ?? breedingPair.maleBirdEntity?.species?.id}
        />

        <BreedingPairOutcomesPanel breedingPairId={breedingPair.id} />
      </BrandModalBody>
      <BrandModalFooter>
        <button
          type="button"
          className="btn btn-secondary w-full sm:w-auto"
          onClick={onClose}
        >
          Close
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
}
